import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Sun, Moon, Menu, Store } from 'lucide-react';
import AdminSidebar from '../admin/layout/AdminSidebar';
import LanguageSwitcher from '../common/LanguageSwitcher';

const pageTitles = {
  '/dashboard': 'Panel Principal',
  '/network': 'Mi Red',
  '/commissions': 'Comisiones',
  '/profile': 'Perfil',
  '/orders': 'Mis Órdenes',
  '/mlm': 'Dashboard MLM',
  '/mlm/binary-tree': 'Árbol Binario',
  '/mlm/unilevel-tree': 'Árbol Unilevel',
  '/mlm/rank-progression': 'Progresión de Rango',
  '/mlm/wallet': 'Wallet',
  '/mlm/sttora-points': 'Sttora Points',
  '/mlm/global-pool': 'Global Partner Pool',
};

const Layout = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const pageTitle = pageTitles[location.pathname] || 'Sttora';

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    handleResize(); // Check on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <AdminSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className={`${sidebarOpen ? 'lg:ml-80' : 'lg:ml-0'} transition-all duration-300`}>
        <header className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-sm border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={toggleSidebar}
                  className={`lg:hidden p-2 rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
                >
                  <Menu className="w-5 h-5" />
                </button>
                <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{pageTitle}</h1>
              </div>
              <div className="flex items-center space-x-4">
                <LanguageSwitcher />
                <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('common.welcome')}, {user?.name}</span>
                <Link
                  to="/store"
                  className={`p-2 rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-orange-400' : 'text-gray-500 hover:text-orange-600'} transition-colors`}
                  title="Tienda STTORA"
                >
                  <Store className="w-5 h-5" />
                </Link>
                <button
                  onClick={toggleTheme}
                  className={`p-2 rounded-lg ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
                >
                  {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => { logout(); window.location.href = '/login'; }}
                  className={`${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
                >
                  {t('common.logout')}
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;